import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import toast from 'react-hot-toast';
import * as swapApi from '../utils/swapApi';
import { storage } from '../utils/storage';

interface User {
  id: string;
  name: string;
  email: string;
}

interface SwapRequest {
  id: string;
  offerId: string;
  userId: string;
  giveDate: Date;
  contact: string | null;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  createdAt: Date;
  updatedAt: Date;
  user: User;
}

export interface SwapOffer {
  id: string;
  userId: string;
  giveDate: Date;
  wantDate: Date;
  reason: string;
  status: 'OPEN' | 'MATCHED' | 'COMPLETED' | 'CANCELLED';
  createdAt: Date;
  updatedAt: Date;
  user: User;
  requests: SwapRequest[];
}

export interface SwapContextType {
  myOffers: SwapOffer[];
  allOffers: SwapOffer[];
  isLoading: boolean;
  loadMyOffers: () => Promise<void>;
  loadAllOffers: () => Promise<void>;
  addOffer: (giveDate: Date, wantDate: Date, reason: string) => Promise<void>;
  cancelOffer: (offerId: string) => Promise<void>;
  addRequest: (offerId: string, giveDate: Date, contact?: string) => Promise<void>;
  acceptRequest: (requestId: string) => Promise<void>;
  rejectRequest: (requestId: string) => Promise<void>;
}

const SwapContext = createContext<SwapContextType | undefined>(undefined);

export const SwapProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [myOffers, setMyOffers] = useState<SwapOffer[]>([]);
  const [allOffers, setAllOffers] = useState<SwapOffer[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadMyOffers = async () => {
    setIsLoading(true);
    try {
      const response = await swapApi.getMyOffers();
      setMyOffers(response.data.offers.map((offer: any) => ({
        ...offer,
        giveDate: new Date(offer.giveDate),
        wantDate: new Date(offer.wantDate),
        createdAt: new Date(offer.createdAt),
        updatedAt: new Date(offer.updatedAt),
        requests: offer.requests.map((req: any) => ({
          ...req,
          giveDate: new Date(req.giveDate),
          createdAt: new Date(req.createdAt),
          updatedAt: new Date(req.updatedAt),
        })),
      })));
    } catch (error: any) {
      console.error('Load my offers error:', error);
      toast.error(error.response?.data?.message || '내 제안 조회 실패');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAllOffers = async () => {
    setIsLoading(true);
    try {
      const response = await swapApi.getAllOffers();
      setAllOffers(response.data.offers.map((offer: any) => ({
        ...offer,
        giveDate: new Date(offer.giveDate),
        wantDate: new Date(offer.wantDate),
        createdAt: new Date(offer.createdAt),
        updatedAt: new Date(offer.updatedAt),
        requests: offer.requests.map((req: any) => ({
          ...req,
          giveDate: new Date(req.giveDate),
          createdAt: new Date(req.createdAt),
          updatedAt: new Date(req.updatedAt),
        })),
      })));
    } catch (error: any) {
      console.error('Load all offers error:', error);
      toast.error(error.response?.data?.message || '제안 조회 실패');
    } finally {
      setIsLoading(false);
    }
  };

  const addOffer = async (giveDate: Date, wantDate: Date, reason: string) => {
    try {
      await swapApi.createOffer(giveDate, wantDate, reason);
      toast.success('교환 제안이 등록되었습니다! 🎉');
      loadMyOffers();
    } catch (error: any) {
      console.error('Add offer error:', error);
      toast.error(error.response?.data?.message || '제안 등록 실패');
      throw error;
    }
  };

  const cancelOffer = async (offerId: string) => {
    try {
      await swapApi.cancelOffer(offerId);
      toast.success('교환 제안이 취소되었습니다.');
      loadMyOffers();
    } catch (error: any) {
      console.error('Cancel offer error:', error);
      toast.error(error.response?.data?.message || '제안 취소 실패');
      throw error;
    }
  };

  const addRequest = async (offerId: string, giveDate: Date, contact?: string) => {
    try {
      await swapApi.createRequest(offerId, giveDate, contact);
      toast.success('교환 요청이 전송되었습니다! 🚀');
      loadAllOffers();
    } catch (error: any) {
      console.error('Add request error:', error);
      toast.error(error.response?.data?.message || '요청 전송 실패');
      throw error;
    }
  };

  const acceptRequest = async (requestId: string) => {
    try {
      await swapApi.acceptRequest(requestId);
      toast.success('교환 요청을 승인했습니다! 🎉');
      loadMyOffers();
    } catch (error: any) {
      console.error('Accept request error:', error);
      toast.error(error.response?.data?.message || '승인 실패');
      throw error;
    }
  };

  const rejectRequest = async (requestId: string) => {
    try {
      await swapApi.rejectRequest(requestId);
      toast.success('교환 요청을 거절했습니다.');
      loadMyOffers();
    } catch (error: any) {
      console.error('Reject request error:', error);
      toast.error(error.response?.data?.message || '거절 실패');
      throw error;
    }
  };

  useEffect(() => {
    // 토큰 있을 때만 데이터 로드
    const token = storage.get('azocsii-token');
    if (token) {
      loadMyOffers();
      loadAllOffers();
    }
  }, []);

  return (
    <SwapContext.Provider
      value={{
        myOffers,
        allOffers,
        isLoading,
        loadMyOffers,
        loadAllOffers,
        addOffer,
        cancelOffer,
        addRequest,
        acceptRequest,
        rejectRequest,
      }}
    >
      {children}
    </SwapContext.Provider>
  );
};

export const useSwap = () => {
  const context = useContext(SwapContext);
  if (!context) {
    throw new Error('useSwap must be used within SwapProvider');
  }
  return context;
};
